import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Inbox, Clock3, ShieldAlert, MessagesSquare, CalendarDays,
  RefreshCcw, ArrowRight, FolderOpen, Timer, FileCheck2, Loader2,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";

const KIND_ICON = {
  shift_claim:       ShieldAlert,
  swap_approval:     RefreshCcw,
  leave_request:     CalendarDays,
  clock_adjustment:  Clock3,
  missed_punch:      Timer,
  timesheet_week:    FileCheck2,
  case_assigned:     FolderOpen,
  sla_breach:        ShieldAlert,
  expiring_record:   MessagesSquare,
};

const KIND_LABEL = {
  shift_claim: "Shift claim",
  swap_approval: "Swap",
  leave_request: "Leave",
  clock_adjustment: "Clock adjustment",
  missed_punch: "Missed clock-out",
  timesheet_week: "Timesheet week",
  case_assigned: "Case",
  sla_breach: "SLA breach",
  expiring_record: "Record",
};

const URGENCY_CHIP = {
  high: "chip-coral",
  med: "chip-amber",
  low: "chip-violet",
};

function fmtAge(ms) {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function Row({ row, onAction, busyId }) {
  const nav = useNavigate();
  const Icon = KIND_ICON[row.kind] || Inbox;
  const isBusy = busyId === row.id;

  function openTarget() {
    if (row.action_url) nav(row.action_url);
  }

  const inline = INLINE_ACTIONS[row.kind];

  return (
    <div
      className="grid grid-cols-[26px_100px_1fr_auto] items-center gap-3 px-3 py-2.5 border-b border-brand-rule hover:bg-brand-hush"
      data-testid={`queue-row-${row.id}`}
    >
      <div className={`h-6 w-6 rounded-md flex items-center justify-center ${row.urgency === "high" ? "bg-[#FEE2E2] text-brand-coral" : row.urgency === "med" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#EDE9FE] text-brand-violet"}`}>
        <Icon size={13} />
      </div>
      <div className="min-w-0">
        <span className={`chip ${URGENCY_CHIP[row.urgency]} mono-num text-[10px]`}>{KIND_LABEL[row.kind] || row.kind}</span>
      </div>
      <div className="min-w-0">
        <button
          type="button"
          onClick={openTarget}
          className="text-[13px] text-brand-ink truncate block text-left hover:underline"
          data-testid={`queue-row-open-${row.id}`}
        >
          {row.title}
        </button>
        {row.subtitle && (
          <div className="text-[11px] text-brand-muted truncate">{row.subtitle}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10.5px] text-brand-muted mono-num">{fmtAge(row.age_ms)}</span>
        {inline && (
          <div className="flex items-center gap-1">
            {inline.map((a) => (
              <button
                key={a.label}
                className={`${a.variant === "primary" ? "btn-violet" : "btn-outline"} text-[11px] h-7`}
                disabled={isBusy}
                onClick={() => onAction(row, a)}
                data-testid={`queue-action-${a.action}-${row.id}`}
              >
                {isBusy && <Loader2 size={11} className="animate-spin" />}
                {a.label}
              </button>
            ))}
          </div>
        )}
        <button
          className="btn-ghost text-[11px] h-7 !px-1.5"
          onClick={openTarget}
          aria-label="Open"
          data-testid={`queue-row-arrow-${row.id}`}
        >
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

const INLINE_ACTIONS = {
  shift_claim: [
    { label: "Approve", action: "approve_claim", variant: "primary" },
    { label: "Reject",  action: "reject_claim",  variant: "" },
  ],
  swap_approval: [
    { label: "Approve", action: "approve_swap", variant: "primary" },
    { label: "Reject",  action: "reject_swap",  variant: "" },
  ],
  leave_request: [
    { label: "Approve", action: "approve_leave", variant: "primary" },
    { label: "Reject",  action: "reject_leave",  variant: "" },
  ],
};

async function performAction(companyId, row, action) {
  const [, id] = row.id.split(":");
  switch (action.action) {
    case "approve_claim":
      return api.post(`/companies/${companyId}/rotas/claims/${id}/approve`, {});
    case "reject_claim":
      return api.post(`/companies/${companyId}/rotas/claims/${id}/reject`, {});
    case "approve_swap":
      return api.post(`/companies/${companyId}/rotas/swaps/${id}/approve`, {});
    case "reject_swap":
      return api.post(`/companies/${companyId}/rotas/swaps/${id}/reject`, {});
    case "approve_leave":
      return api.patch(`/companies/${companyId}/hr/leave/${id}`, { status: "approved" });
    case "reject_leave":
      return api.patch(`/companies/${companyId}/hr/leave/${id}`, { status: "rejected" });
    default:
      throw new Error("Unknown action");
  }
}

export default function Queue() {
  const { companyId } = useParams();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [kindFilter, setKindFilter] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/queue`);
      setData(data);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load the queue");
    }
  }, [companyId]);

  React.useEffect(() => { load(); }, [load]);

  // Live-refresh on any relevant record event
  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type || "";
      if (t.startsWith("case.") || t.startsWith("leave.") || t.startsWith("swap.") ||
          t.startsWith("claim.") || t.startsWith("timesheet.") || t.startsWith("clock.") ||
          t.startsWith("rule_event.")) {
        load();
      }
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  async function onAction(row, action) {
    setBusyId(row.id);
    try {
      await performAction(companyId, row, action);
      toast.success("Done");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't apply");
    } finally { setBusyId(null); }
  }

  const rows = data?.rows || [];
  const filtered = kindFilter ? rows.filter((r) => r.kind === kindFilter) : rows;
  const totals = data?.totals || { by_kind: {}, by_urgency: {}, count: 0 };

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="queue-page">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="h-8 w-8 rounded-md bg-[#EDE9FE] text-brand-violet flex items-center justify-center">
          <Inbox size={17} />
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Queue</div>
          <div className="text-[11.5px] text-brand-muted">Everything awaiting a decision you can take.</div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          <span className="chip chip-coral mono-num text-[10.5px]" data-testid="queue-count-high">{totals.by_urgency.high || 0} high</span>
          <span className="chip chip-amber mono-num text-[10.5px]" data-testid="queue-count-med">{totals.by_urgency.med || 0} med</span>
          <span className="chip chip-violet mono-num text-[10.5px]" data-testid="queue-count-low">{totals.by_urgency.low || 0} low</span>
        </div>
      </div>

      <div className="card-panel mb-3">
        <div className="px-3 py-2 flex flex-wrap items-center gap-1.5">
          <button
            className={`chip mono-num text-[10.5px] ${kindFilter === "" ? "chip-violet" : ""}`}
            onClick={() => setKindFilter("")}
            data-testid="queue-filter-all"
          >
            All · {totals.count}
          </button>
          {Object.entries(totals.by_kind).map(([k, v]) => (
            <button
              key={k}
              className={`chip mono-num text-[10.5px] ${kindFilter === k ? "chip-violet" : ""}`}
              onClick={() => setKindFilter(k)}
              data-testid={`queue-filter-${k}`}
            >
              {KIND_LABEL[k] || k} · {v}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="card-panel p-4" data-testid="queue-error">
          <div className="text-[13px] font-semibold text-brand-ink">Couldn't load your queue</div>
          <div className="text-[12px] text-brand-muted mt-1 mb-2">{error}</div>
          <button className="btn-outline text-[12px] h-8" onClick={load}>Try again</button>
        </div>
      )}

      {!error && (
        <div className="card-panel">
          {data == null ? (
            <div className="p-4 space-y-2" data-testid="queue-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-11 rounded bg-brand-hush animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center" data-testid="queue-empty">
              <div className="text-[13.5px] font-semibold text-brand-ink">Inbox zero</div>
              <div className="text-[12px] text-brand-muted mt-1">
                Nothing needs your decision right now. New items land here live.
              </div>
            </div>
          ) : (
            filtered.map((r) => (
              <Row key={r.id} row={r} onAction={onAction} busyId={busyId} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
